import React, { useState, useRef } from 'react'
import {
  Box,
  IconButton,
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItemText,
  Tooltip
} from '@mui/material'
import {
  MoreVert,
  Edit,
  DeleteOutlineRounded, Sync
} from '@mui/icons-material'
import { useTheme } from '@emotion/react'
import { apiPost } from './Api_Functions'
// ⚠️⚠️ EL HUECO DEL ICONO, COMPARTIDO POR TODAS LAS OPCIONES DEL MENÚ.
//
// «Open detail» medía 36 px de alto con el icono a 8 px del borde, y «Delete» 52 px con el icono a
// 16 — DECLARANDO EL MISMO PADDING. El padding no era lo que mandaba: la diferencia estaba en que
// la opción de borrar envolvía su icono en un `<IconButton>` —con su propio relleno y su tamaño
// mínimo— y las acciones a medida lo pintaban a pelo.
//
// 📌 Y NO SE ARREGLA ENVOLVIENDO LAS OTRAS EN UN `IconButton`: DIEZ acciones a medida de la
// aplicación ya devuelven su propio `<IconButton>` con su `onClick`, así que envolverlas metería un
// botón dentro de otro — HTML inválido y clics rotos en diez pantallas.
//
// Se iguala por el otro lado: un hueco NEUTRO del mismo ancho en las CUATRO ramas. De paso
// desaparecen los `IconButton` de Borrar, Editar y Resincronizar, y con ellos un `<button>` no
// pulsable (`pointerEvents: none`) dentro de un `role="menuitem"` — un control que no controlaba
// nada y que el lector de pantalla anunciaba igual.
//
// ⚠️ Editar y Resincronizar SE ME PASARON en la primera pasada: medí «Open detail» contra «Delete»,
// las dos quedaron a la misma altura, y di el menú por igualado. Dos de las cuatro ramas seguían a
// 52 px. Cuando se iguala algo, hay que enumerar TODO lo que se iguala, no mirar la pareja que se
// tenía delante.
const ICON_SLOT_SX = { width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }

const WithDeleteTooltip = ({ title, children }) => title
  ? <Tooltip title={title} placement='left'><span>{children}</span></Tooltip>
  : children

const ActionsMenu = ({
  row,
  actions,
  editIndex,
  setEditIndex,
  onEditIndexChanged,
  editingRow,
  dataFetch,
  requestPagination,
  smartTableIndexKey,
  multipleSelectionValue,
  setOpenDeleteDialog,
  setCurrentSelectedRow,
  setDisableRowActions,
  t_,
  setNotSavedEditingRow,
  rowHasTableIndexDefined
}) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const theme = useTheme()
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }
  const handleResync = async (id, resource, documentType, documentResourceId) => {
    if (documentType) {
      await apiPost('resyncResource?id=' + id + '&resource=' + resource + '&documentResourceType=' + documentType + '&documentResourceId=' + documentResourceId)
    } else {
      await apiPost('resyncResource?id=' + id + '&resource=' + resource)
    }
    setOpen(false)
  }

  // Story 186 (S89): a disabled Delete may say why. Only when `disabledTooltip(row)` returns text the option is wrapped
  // (a disabled item takes no hover on its own); otherwise the menu is rendered exactly as before.
  const deleteDisabledTooltip = actions.delete?.disabledButton?.(row) ? (actions.delete.disabledTooltip?.(row) || '') : ''

  const hasVisibleContextCustomAction = actions.custom?.some(action =>
    action.contextMenu !== false &&
    action.visible !== false &&
    !action.hide?.(row)
  )
  const hasVisibleEditAction = actions.edit !== undefined &&
    actions.edit &&
    !actions.edit.redirect &&
    (actions.edit.visibilityButton === undefined || actions.edit.visibilityButton(row))
  const hasVisibleResyncAction = actions.resync !== undefined &&
    actions.resync &&
    (actions.resync.visibilityButton === undefined || actions.resync.visibilityButton(row))
  const hasVisibleDeleteAction = actions.delete &&
    (actions.delete.visibilityButton === undefined || actions.delete.visibilityButton(row))

  if (!hasVisibleContextCustomAction && !hasVisibleEditAction && !hasVisibleResyncAction && !hasVisibleDeleteAction) {
    return null
  }

  return (
    <>
      <IconButton ref={anchorRef} onClick={handleToggle} sx={{ backgroundColor: theme.palette.primary.dark, opacity: '0.80', padding: '5px' }}>
        <MoreVert />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement='bottom-end'
        sx={{
          zIndex: 9999,
          position: 'relative'
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper sx={{ zIndex: 9999 }}>
              <ClickAwayListener onClickAway={handleClose}>
                {/* Every entry is ICON_SLOT_SX (40px wide, glyph centred) followed by its label.
                    8px of item padding plus the 8px the slot leaves around a 24px glyph put it
                    16px from the left edge, matching the 16px the label leaves on the right.
                    minHeight 44 is the minimum touch target of the redesign (HANDOFF 2.5). */}
                <MenuList autoFocusItem={open} sx={{ zIndex: 9999, padding: 0, '& .MuiMenuItem-root': { pl: 1, pr: 2, minHeight: 44 } }}>
                  {actions.custom &&
                    actions.custom.length > 0 &&
                    actions.custom.map((action, index) => {
                      const content = action.render ? action.render(row, dataFetch, requestPagination) : null
                      const label = typeof action.name === 'function'
                        ? action.name(row)
                        : (action.name || ' ')
                      const color = typeof action.color === 'function'
                        ? action.color(row)
                        : 'primary'
                      // If any MenuItem doesnt work but the IconButton does, move the onClick to the IconButton.
                      // Algo copy outside disable conditions to the whole item.
                      // Example below:
                      /*
                      BEFORE:
                      name: 'Resume FO',
                      render: (row) => {
                        return (
                          <IconButton
                            disabled={editing || !isUserAuthorized('RESUME_FO_FABRICATION_ORDERS')}
                            onClick={async () => handlePauseResumeClick('resume', row.id)}
                          >
                            <PlayCircleFilledOutlined />
                          </IconButton>
                        )
                      }
                      AFTER:
                      name: 'Copy as',
                      render: (row) => {
                        return (
                          <IconButton>
                            <ContentCopyIcon />
                          </IconButton>
                        )
                      },
                      onClick: (row) => handleCopyButtonClick(row),
                      disabled: editing || !isUserAuthorized('COPY_FABRICATION_ORDERS')
                      */
                      const handleActionClick = () => {
                        const isDisabled = action.disabled && typeof action.disabled === 'function'
                          ? action.disabled(row)
                          : action.disabled
                        if (!isDisabled) {
                          if (action.onClick && typeof action.onClick === 'function') {
                            action.onClick(row, dataFetch, requestPagination)
                          }
                        }
                        setOpen(false)
                      }
                      const isDisabled = action.disabled && typeof action.disabled === 'function'
                        ? action.disabled(row)
                        : action.disabled
                      return (
                        <MenuItem
                          key={'custom_action_' + index}
                          onClick={handleActionClick}
                          disabled={isDisabled}
                        >
                          {content && (
                            <Box sx={ICON_SLOT_SX}>
                              {React.cloneElement(content, {
                                sx: { pointerEvents: action.onClick ? 'none' : 'auto' } // To avoid disabling the IconButton when onClick is inside IconButton (migrate)
                              })}
                            </Box>
                          )}
                          <ListItemText
                            primary={t_(label)}
                            style={{ color: color === 'primary' ? theme.palette.primary.main : theme.palette.warning.main }}
                          />
                        </MenuItem>
                      )
                    })}
                  {actions.edit !== undefined && actions.edit && !actions.edit.redirect &&
                    (actions.edit.visibilityButton === undefined || actions.edit.visibilityButton(row))
                    ? (
                      <MenuItem
                        disabled={
                          (actions.edit.disabled !== null ? typeof actions.edit.disabled === 'function' ? actions.edit.disabled(row) : actions.edit.disabled : false) ||
                          editIndex >= -1 || (actions.edit.condition && typeof actions.edit.condition === 'function' && !actions.edit.condition(row))
                        }
                        style={{ visibility: (actions.edit.visibilityButton !== undefined ? (!actions.edit.visibilityButton(row) ? 'hidden' : 'visible') : 'visible') }}
                        onClick={() => {
                          setOpen(false)
                          if (rowHasTableIndexDefined(row)) {
                            if (editingRow !== undefined) {
                              editingRow(true)
                            }
                            setEditIndex(row[smartTableIndexKey])
                            if (onEditIndexChanged) {
                              onEditIndexChanged(row[smartTableIndexKey], row)
                            }
                            setNotSavedEditingRow({ ...row })
                          }
                        }}
                      >
                        <Box sx={ICON_SLOT_SX}>
                          <Edit />
                        </Box>
                        <ListItemText
                          primary={t_('Edit')}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </MenuItem>
                      )
                    : null}
                  {actions.resync !== undefined && actions.resync && (actions.resync.visibilityButton === undefined || actions.resync.visibilityButton(row))
                    ? (
                      <MenuItem
                        disabled={actions.resync.disabled}
                        style={{ visibility: (actions.resync.visibilityButton !== undefined ? (!actions.resync.visibilityButton(row) ? 'hidden' : 'visible') : 'visible') }}
                        onClick={() => {
                          handleResync(row.id, actions.resync.resource, actions.resync.documentType, actions.resync.documentResourceId)
                        }}
                      >
                        <Box sx={ICON_SLOT_SX}>
                          <Sync />
                        </Box>
                        <ListItemText
                          primary={t_('Resync')}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </MenuItem>
                      )
                    : null}
                  {actions.delete &&
                    (actions.delete.visibilityButton === undefined || actions.delete.visibilityButton(row)) && (
                      <WithDeleteTooltip title={deleteDisabledTooltip}>
                        <MenuItem
                          disabled={actions.delete.disabled || editIndex >= -1 || multipleSelectionValue.rows.length !== 0 || multipleSelectionValue.allSelected || (actions.delete.disabledButton !== undefined ? actions.delete.disabledButton(row) : false) || (actions.delete.condition && typeof actions.delete.condition === 'function' && !actions.delete.condition(row))}
                          onClick={() => {
                            setOpen(false)
                            if (
                              typeof actions.delete
                                .fetchFunction === 'function'
                            ) {
                              const auxSelectionValue = []
                              auxSelectionValue[0] = row
                              if (setDisableRowActions !== undefined) setDisableRowActions(true)
                              setOpenDeleteDialog(true)
                              setCurrentSelectedRow(row)
                            }
                          }}
                        >
                          <Box sx={ICON_SLOT_SX}>
                            <DeleteOutlineRounded />
                          </Box>
                          <ListItemText
                            primary={t_('Delete')}
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </MenuItem>
                      </WithDeleteTooltip>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default ActionsMenu
